import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { ImageWithFallback } from '../ImageWithFallback';

type Product = {
  id: string;
  name: string;
  sku?: string;
  image?: string;
  image_urls?: string[];
  images?: Array<{
    originalUrl?: string;
    thumbnailUrl?: string;
    url?: string;
  }>;
  price?: number;
  description?: string;
  sizes?: string[];
  colors?: string[];
  material?: string;
  pattern?: string;
  category?: string;
};

type ProductRecommendationsProps = {
  products: Product[];
  onProductClick?: (product: Product) => void;
  isAdmin?: boolean;
};

export function ProductRecommendations({
  products,
  onProductClick,
  isAdmin = false,
}: ProductRecommendationsProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : products.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < products.length - 1 ? prev + 1 : 0));
  };

  if (products.length === 0) return null;

  const currentProduct = products[currentIndex];

  // Get image URL from various possible sources
  const getImageUrl = () => {
    if (currentProduct.images?.[0]?.originalUrl) {
      return currentProduct.images[0].originalUrl;
    }
    if (currentProduct.images?.[0]?.url) {
      return currentProduct.images[0].url;
    }
    if (currentProduct.image_urls?.[0]) {
      return currentProduct.image_urls[0];
    }
    if (currentProduct.image) {
      return currentProduct.image;
    }
    return '';
  };

  const imageUrl = getImageUrl();
  const isStorageUrl = !imageUrl.startsWith('http');

  return (
    <div
      className={`mt-2 space-y-3 w-full max-w-[400px] ${
        isAdmin ? 'ml-auto' : ''
      }`}
    >
      <Card
        className="p-4 bg-card cursor-pointer hover:bg-accent/50 transition-colors"
        onClick={() => onProductClick?.(currentProduct)}
      >
        <div className="space-y-3">
          {/* Product Image */}
          <div className="relative w-full">
            <ImageWithFallback
              isStorageUrl={isStorageUrl}
              src={imageUrl}
              alt={currentProduct.name}
              className="w-full h-64 object-contain rounded-lg"
            />
          </div>

          {/* Product Info - Title, SKU, and Price */}
          <div className="space-y-2">
            <h4 className="font-medium text-base">{currentProduct.name}</h4>
            <div className="flex items-center justify-between gap-2">
              {currentProduct.sku && (
                <p className="text-sm text-muted-foreground">
                  SKU: {currentProduct.sku}
                </p>
              )}
              {currentProduct.price && (
                <p className="text-sm font-semibold text-primary">
                  ${currentProduct.price}
                </p>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Pagination Controls */}
      {products.length > 1 && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handlePrevious();
            }}
            className="gap-1"
          >
            <ChevronLeft className="size-4" />
            Previous
          </Button>

          <span className="text-sm text-muted-foreground">
            {currentIndex + 1} / {products.length}
          </span>

          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            className="gap-1"
          >
            Next
            <ChevronRight className="size-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
